import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  Row,
  Select
} from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import {
  useTreatmentServiceCreatePregnancyMonitoringEndpoint,
  useTreatmentServiceGetPregnancyMonitoringEndpoint,
  useTreatmentServiceGetPregnancyMonitoringListEndpointKey,
  useTreatmentServiceUpdatePregnancyMonitoringEndpoint
} from 'src/api/queries'
import {
  ApiError,
  InterruptedPregnancyReason,
  PregnancyActionStatus,
  PregnancyLocalization,
  PregnancyType
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { message, SaveConfirm } from 'src/components'
import {
  calculateEstimatedDueDate,
  calculateGestationalAge,
  calculateLmpFromGestationalAge
} from 'src/features/persons/utils'
import { dayMonthYearBackSlash, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler, toDayjs } from 'src/utils'

type PregnancyValues = {
  lastMenstrualPeriod?: dayjs.Dayjs
  gestationalAgeInWeeks?: number
}

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

export const PersonPregnancyCreateModal = ({
  id,
  onCancel,
  ...props
}: Props) => {
  const [form] = Form.useForm()

  const queryClient = useQueryClient()
  const { t } = useLocales()

  const { id: personId } = useParams()

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [useTreatmentServiceGetPregnancyMonitoringListEndpointKey]
    })
    onCancel()
  }

  const { mutateAsync: addPregnancy, isPending } =
    useTreatmentServiceCreatePregnancyMonitoringEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        onSuccess()
        message.success(t('pregnancy.pregnancy_created_successfully'))
      }
    })

  const { mutateAsync: updatePregnancy, isPending: isUpdatePending } =
    useTreatmentServiceUpdatePregnancyMonitoringEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        onSuccess()
        message.success(t('pregnancy.pregnancy_edit_successfully'))
      }
    })

  const { data: pregnancyDetails, isLoading } =
    useTreatmentServiceGetPregnancyMonitoringEndpoint(
      {
        id: String(id)
      },
      [String(id)],
      {
        enabled: !!id
      }
    )

  const onSubmit = async () => {
    const values = await form.validateFields()

    if (id) {
      updatePregnancy({
        id: id,
        requestBody: {
          interruptedPregnancyReason: values?.interruptedPregnancyReason,
          pregnancyActionStatus: values?.pregnancyActionStatus,
          pregnancyStatusChangedDate: toDayjs(
            values?.pregnancyStatusChangedDate
          )?.format(yearMonthDaySlash),
          pregnancyStatusChangedComment: values?.pregnancyStatusChangedComment
        }
      })
    } else {
      addPregnancy({
        requestBody: {
          lastMenstrualPeriod: dayjs(values?.lastMenstrualPeriod).format(
            yearMonthDaySlash
          ),
          gestationalAgeInWeeks: values?.gestationalAgeInWeeks,
          pregnancyType: values?.pregnancyType,
          pregnancyStatusChangedDate: toDayjs(
            values?.pregnancyStatusChangedDate
          )?.format(yearMonthDaySlash),
          pregnancyStatusChangedComment: values?.pregnancyStatusChangedComment,
          personId: personId,
          estimatedBirthDate: toDayjs(values?.estimated_birth_date)?.format(
            yearMonthDaySlash
          ),
          pregnancyLocalization: values.pregnancyLocalization
        }
      })
    }
  }

  const pregnancyActionStatus = Form.useWatch('pregnancyActionStatus', form)

  const pregnancyTypeOptions = Object.values(PregnancyType).map((item) => ({
    value: item,
    label: t(`pregnancy.pregnancy_type_status.${item}`)
  }))

  const pregnancyActionStatusOptions = Object.values(PregnancyActionStatus).map(
    (item) => ({
      value: item,
      label: t(`pregnancy.pregnancy_actions_statuses.${item}`)
    })
  )

  const interruptedPregnancyReasonOptions = Object.values(
    InterruptedPregnancyReason
  ).map((item) => ({
    value: item,
    label: t(`pregnancy.interrupted_pregnancy_reasons.${item}`)
  }))

  const localizationOptions = Object.values(PregnancyLocalization).map(
    (item) => ({
      value: item,
      label: t(`pregnancy.pregnant_localization_status.${item}`)
    })
  )

  const onValuesChange = (changedValues: AnyObject) => {
    onPregnancyValuesChange(changedValues)
  }

  const onPregnancyValuesChange = (changedValues: PregnancyValues) => {
    if (changedValues?.lastMenstrualPeriod) {
      const lmp = changedValues?.lastMenstrualPeriod.format(yearMonthDaySlash)

      form.setFieldsValue({
        gestationalAgeInWeeks: calculateGestationalAge(lmp),
        estimated_birth_date: dayjs(calculateEstimatedDueDate(lmp))
      })
    }

    if (changedValues?.gestationalAgeInWeeks !== undefined) {
      const weeks = changedValues?.gestationalAgeInWeeks
      if (!isNaN(weeks) && weeks > 0) {
        const lmpDate = calculateLmpFromGestationalAge(weeks)
        form.setFieldsValue({
          lastMenstrualPeriod: dayjs(lmpDate),
          estimated_birth_date: dayjs(calculateEstimatedDueDate(lmpDate))
        })
      }
    }
  }

  useEffect(() => {
    if (pregnancyDetails && form) {
      form.setFieldsValue({
        gestationalAgeInWeeks: pregnancyDetails?.gestationalAgeInWeeks,
        lastMenstrualPeriod: toDayjs(pregnancyDetails?.lastMenstrualPeriod),
        estimated_birth_date: toDayjs(pregnancyDetails?.expectedDateOfBirth),
        pregnancyType: pregnancyDetails?.pregnancyType,
        pregnancyStatusChangedDate: toDayjs(
          pregnancyDetails?.pregnancyStatusChangedDate
        ),
        interruptedPregnancyReason:
          pregnancyDetails?.interruptedPregnancyReason,
        pregnancyStatusChangedComment:
          pregnancyDetails?.pregnancyStatusChangedComment,
        pregnancyMonitoringStatus: pregnancyDetails?.pregnancyMonitoringStatus,
        pregnancyLocalization: pregnancyDetails?.pregnancyLocalization,
        pregnancyActionStatus: pregnancyDetails?.pregnancyActionStatus
      })
    }
  }, [pregnancyDetails, form])

  return (
    <Modal
      width={1000}
      open
      centered
      maskClosable={false}
      loading={isLoading}
      onCancel={onCancel}
      title={t('pregnancy.pregnancy')}
      footer={null}
      {...props}
    >
      <Card>
        <Flex vertical gap={16}>
          <Form
            form={form}
            layout="vertical"
            onValuesChange={onValuesChange}
            requiredMark={false}
          >
            <Flex gap={24} vertical>
              <Flex vertical gap={24}>
                <Row gutter={[24, 24]}>
                  <Col span={24}>
                    <Form.Item
                      name={'gestationalAgeInWeeks'}
                      label={t('pregnancy.age')}
                      rules={requiredRules?.concat({
                        max: 40,
                        type: 'number'
                      })}
                    >
                      <InputNumber
                        placeholder={t('pregnancy.age')}
                        maxLength={40}
                        className="full-width"
                        disabled={!!id}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="lastMenstrualPeriod"
                      label={t('pregnancy.last_gestation_date')}
                      rules={requiredRules}
                    >
                      <DatePicker
                        className="full-width"
                        format={dayMonthYearBackSlash}
                        disabled={!!id}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="estimated_birth_date"
                      label={t('pregnancy.estimated_birth_date')}
                      rules={requiredRules}
                    >
                      <DatePicker
                        className="full-width"
                        format={dayMonthYearBackSlash}
                        disabled={!!id}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name={'pregnancyType'}
                      label={t('pregnancy.pregnancy_type')}
                      rules={requiredRules}
                    >
                      <Select
                        options={pregnancyTypeOptions}
                        placeholder={t('pregnancy.pregnancy_type')}
                        disabled={!!id}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name={'pregnancyLocalization'}
                      label={t('pregnancy.pregnancy_localization')}
                      rules={requiredRules}
                    >
                      <Select
                        options={localizationOptions}
                        disabled={!!id}
                        placeholder={t('pregnancy.pregnancy_localization')}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Flex>

              <Row gutter={[24, 24]}>
                {!!id && (
                  <>
                    <Col span={12}>
                      <Form.Item
                        name={'pregnancyActionStatus'}
                        label={t('pregnancy.pregnancy_status')}
                        rules={requiredRules}
                      >
                        <Select
                          options={pregnancyActionStatusOptions}
                          placeholder={t('pregnancy.pregnancy_status')}
                        />
                      </Form.Item>
                    </Col>

                    {pregnancyActionStatus ===
                      PregnancyActionStatus.INTERRUPTED_PREGNANCY && (
                      <Col span={12}>
                        <Form.Item
                          name={'interruptedPregnancyReason'}
                          label={t('pregnancy.interrupted_pregnancy_reason')}
                          rules={requiredRules}
                        >
                          <Select
                            options={interruptedPregnancyReasonOptions}
                            placeholder={t(
                              'pregnancy.interrupted_pregnancy_reason'
                            )}
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </>
                )}

                <Col span={12}>
                  <Form.Item
                    name={'pregnancyStatusChangedDate'}
                    label={t('common.date')}
                  >
                    <DatePicker
                      className="full-width"
                      format={dayMonthYearBackSlash}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    name={'pregnancyStatusChangedComment'}
                    label={t('common.comment')}
                  >
                    <Input.TextArea
                      rows={5}
                      placeholder={t('common.leave_comment')}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Flex>
          </Form>
          <Flex justify="center" gap={8}>
            <Button onClick={onCancel}>{t('common.cancel')}</Button>

            <SaveConfirm
              title={`${t('common.confirm')}?`}
              onConfirm={onSubmit}
              okButtonProps={{ loading: isPending || isUpdatePending }}
            >
              <Button type="primary">{t('common.confirm')}</Button>
            </SaveConfirm>
          </Flex>
        </Flex>
      </Card>
    </Modal>
  )
}
